<script>
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import SettingsCard from './components/SettingsCard.vue';
import TierModal from './components/TierModal.vue';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { CARD_CLASS } from './constants';
import { describeMinutes, TIER_STYLE } from './helpers';

export default {
  name: 'CoLivingSlaPage',
  components: {
    PageHeader,
    LoadingState,
    CoLivingTabs,
    SettingsCard,
    TierModal,
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      tierStyle: TIER_STYLE,
      editingTier: null,
      loadError: '',
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', {
      tiers: 'getSlaTiers',
      roles: 'getRoles',
      uiFlags: 'getUiFlags',
    }),
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions('coLivingConfig', ['fetchSla', 'fetchSettings']),
    async load() {
      this.loadError = '';
      try {
        await Promise.all([this.fetchSla(), this.fetchSettings()]);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    minutesText(minutes, basis) {
      const { unit, value, working } = describeMinutes(minutes, basis);
      const base = this.$tc(`CO_LIVING.SLA.MINUTES.${unit}`, value, {
        n: value,
      });
      return working && unit !== 'NOW'
        ? `${base} ${this.$t('CO_LIVING.SLA.MINUTES.WORKING_SUFFIX')}`
        : base;
    },
    roleLabel(key) {
      const role = this.roles.find(r => r.key === key);
      return role ? role.label : key || this.$t('CO_LIVING.COMMON.NONE');
    },
    goToCalendar() {
      this.$router.push({
        name: 'co_living_calendar',
        params: { accountId: this.$route.params.accountId },
      });
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-sla-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <LoadingState
      v-if="uiFlags.isFetchingSla && !tiers.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else class="flex flex-col gap-6">
      <section>
        <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ $t('CO_LIVING.SLA.TIERS_TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {{ $t('CO_LIVING.SLA.TIERS_DESCRIPTION') }}
        </p>
        <p class="mt-1 mb-3 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.SLA.PLACEHOLDER_NOTE') }}
        </p>

        <div
          class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
        >
          <table class="w-full text-sm" data-testid="co-living-tier-table">
            <thead>
              <tr class="text-left text-slate-500 dark:text-slate-400">
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.TIER') }}</th>
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.BASIS') }}</th>
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.ACK') }}</th>
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.ETA') }}</th>
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.ESCALATE') }}</th>
                <th class="p-3">{{ $t('CO_LIVING.SLA.COLUMNS.FALLBACK') }}</th>
                <th class="p-3" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in tiers"
                :key="row.tier"
                class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                :data-testid="`co-living-tier-row-${row.tier}`"
              >
                <td class="p-3">
                  <span
                    class="px-2 py-0.5 mr-2 text-xs font-medium rounded"
                    :class="tierStyle[row.tier]"
                  >
                    {{ row.tier }}
                  </span>
                  {{ $t(`CO_LIVING.SLA.TIER_NAMES.${row.tier}`) }}
                </td>
                <td class="p-3">
                  {{
                    $t(
                      `CO_LIVING.SLA.BASIS.${
                        row.basis === '24x7' ? '24X7' : 'WORKING'
                      }`
                    )
                  }}
                </td>
                <td class="p-3">{{ minutesText(row.ack, row.basis) }}</td>
                <td class="p-3">{{ minutesText(row.eta, row.basis) }}</td>
                <td class="p-3">{{ minutesText(row.escalate, row.basis) }}</td>
                <td class="p-3">{{ roleLabel(row.fallback_role) }}</td>
                <td class="p-3 text-right">
                  <woot-button
                    variant="clear"
                    size="small"
                    icon="edit"
                    :data-testid="`co-living-tier-edit-${row.tier}`"
                    @click="editingTier = row"
                  >
                    {{ $t('CO_LIVING.COMMON.EDIT') }}
                  </woot-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <SettingsCard card-id="clock" />

      <section
        class="flex items-center justify-between gap-4"
        :class="[cardClass]"
        data-testid="co-living-sla-calendar-link"
      >
        <div>
          <h3 class="text-base font-medium text-slate-900 dark:text-slate-100">
            {{ $t('CO_LIVING.SLA.CALENDAR_LINK_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {{ $t('CO_LIVING.SLA.CALENDAR_LINK_DESCRIPTION') }}
          </p>
        </div>
        <woot-button color-scheme="secondary" @click="goToCalendar">
          {{ $t('CO_LIVING.SLA.CALENDAR_LINK_ACTION') }}
        </woot-button>
      </section>
    </div>

    <TierModal
      v-if="editingTier"
      :tier="editingTier"
      @close="editingTier = null"
    />
  </div>
</template>
